import React from "react";
import { OtStepperProps } from "../types/ot.types";
import { useOtStepper } from "../hooks/useOtStepper";
import { useOtStepperTour } from "../hooks/useOtStepperTour";
import { TourGuideButtons } from "../../../shared/tours/TourGuideButtons";

import { Step1Agendar } from "./steps/Step1Agendar";
import { Step2Ingreso } from "./steps/Step2Ingreso";
import { Step3Diagnostico } from "./steps/Step3Diagnostico";
import { Step4Evidencias } from "./steps/Step4Evidencias";
import { Step8FirmaPago } from "./steps/Step8FirmaPago";
import { Step9Entrega } from "./steps/Step9Entrega";
import { Step5AprobacionCliente } from "./steps/Step5AprobacionCliente";
import { Step6AprobacionAdmin } from "./steps/Step6AprobacionAdmin";
import { OrdenCompraDownloadButton } from "./OrdenCompraDownloadButton";
import { getMinDateTimeForField } from "../utils/otDateValidation";
import { ConfirmModal } from "../../../shared/components/ConfirmModal";

export const OtStepper: React.FC<OtStepperProps> = (props) => {
  const { state, actions } = useOtStepper(props);
  const { Tour, startGeneralTour, startStepTour } = useOtStepperTour(state.isLoadingInitial, state.activeStep);
  const isSysadmin = state.role?.role === "SYSADMIN";

  const renderStepContent = () => {
    const cadena = state.otFechasCadena;
    switch (state.activeStep) {
      case 1:
        return (
          <Step1Agendar customGarages={state.customGarages} usedCustomGarages={state.usedCustomGarages} selectedCustomGarage={state.selectedCustomGarage} setSelectedCustomGarage={actions.setSelectedCustomGarage} selectedGarage={state.selectedGarage} setSelectedGarage={actions.setSelectedGarage} appointments={state.appointments} stepOneFormAppointment={state.stepOneFormAppointment} setStepOneFormAppointment={actions.setStepOneFormAppointment} detalleSolicitud={state.detalleSolicitud} archivosRequerimiento={state.archivosRequerimiento} setArchivosRequerimiento={actions.setArchivosRequerimiento} isCompleted={state.maxReachedStep > 1} minFechaCita={getMinDateTimeForField("fecha_cita", cadena)} />
        );
      case 2:
        return (
          <Step2Ingreso imagenesIngreso={state.imagenesIngreso} setImagenesIngreso={actions.setImagenesIngreso} selectedGarage={state.selectedGarage} detalleSolicitud={state.detalleSolicitud} appointments={state.appointments} stepOneFormAppointment={state.stepOneFormAppointment} stepTwoFormAppointment={state.stepTwoFormAppointment} setStepTwoFormAppointment={actions.setStepTwoFormAppointment} isCompleted={state.maxReachedStep > 2} minFechaRecepcion={getMinDateTimeForField("fecha_recepcion", cadena)} />
        );
      case 3:
        return (
          <Step3Diagnostico detallesOT={state.detallesOT} causas={state.causas} categories={state.categories} subcategoriesCache={state.subcategoriesCache} detailsCache={state.detailsCache} updateDetalle={actions.updateDetalle} deleteDetalle={actions.deleteDetalle} addNewConcept={actions.addNewConcept} fillSubcategory={actions.fillSubcategory} fillDetailSubcategory={actions.fillDetailSubcategory} archivoCotizacion={state.archivoCotizacion} setArchivoCotizacion={actions.setArchivoCotizacion} fechaEstimadaEntrega={state.fechaEstimadaEntrega} setFechaEstimadaEntrega={actions.setFechaEstimadaEntrega} isCompleted={state.maxReachedStep > 3} minFechaEstimadaEntrega={getMinDateTimeForField("fecha_estimada_entrega", cadena)} />
        );
      case 4:
        return (
          <Step4Evidencias detallesOT={state.detallesOT} evidenciasReparacion={state.evidenciasReparacion} evidenciasCierre={state.evidenciasCierre} setEvidenciasReparacion={actions.setEvidenciasReparacion} setEvidenciasCierre={actions.setEvidenciasCierre} excepcionesEvidencia={state.excepcionesEvidencia} setExcepcionesEvidencia={actions.setExcepcionesEvidencia} isCompleted={state.maxReachedStep > 4} />
        );
      case 5: {
        const isEjecutivo = state.role?.role === "Ejecutivo";
        const isSysAdmin = state.role?.role === "SYSADMIN" || state.role?.key_role === "SYS_ADMIN";
        const puedeVer = isEjecutivo || isSysAdmin || !!state.aprobacionOt?.puede_ver;
        const isCompleted = state.sysadminEditMode ? false : state.maxReachedStep > 5;
        const readOnly = state.sysadminEditMode ? false : (isCompleted || (!puedeVer && !isEjecutivo));
        return (
          <Step5AprobacionCliente detallesOT={state.detallesOT} updateDetalle={actions.updateDetalle} categories={state.categories} subcategoriesCache={state.subcategoriesCache} detailsCache={state.detailsCache} evidenciasReparacion={state.evidenciasReparacion} uploadVoBoDocument={actions.uploadVoBoDocument} rechazarOrdenAEjecutivo={actions.rechazarOrdenAEjecutivo} isCompleted={isCompleted} readOnly={readOnly} userRole={state.role?.role} aprobacionOt={state.aprobacionOt} voboGeneralUrl={state.voboGeneralUrl} voboGeneralFile={state.voboGeneralFile} detalleOt={state.detalleOt} archivoCotizacion={state.archivoCotizacion} selectedGarage={state.selectedGarage} ordenId={props.orden} />
        );
      }
      case 6: {
        const canEdit = ["SYSADMIN", "Super Administrador", "Super Admin", "Administrador Contraloria"].includes(state.role?.role || "");
        const isCompleted = state.sysadminEditMode ? false : state.maxReachedStep > 6;
        const readOnly = state.sysadminEditMode ? false : (isCompleted || !canEdit);
        return (
          <Step6AprobacionAdmin detallesOT={state.detallesOT} updateDetalle={actions.updateDetalle} categories={state.categories} subcategoriesCache={state.subcategoriesCache} detailsCache={state.detailsCache} evidenciasReparacion={state.evidenciasReparacion} isCompleted={isCompleted} readOnly={readOnly} userRole={state.role?.role} detalleOt={state.detalleOt} rechazarOrdenAEjecutivo={actions.rechazarOrdenAEjecutivo} archivoCotizacion={state.archivoCotizacion} selectedGarage={state.selectedGarage} ordenId={props.orden} />
        );
      }
      case 8:
        const isImmediatePayment = state.proveedor?.clave_periodo_pago == 1;
        const readOnlyDueToCredit = !isImmediatePayment;
        const isActuallyCompleted = state.maxReachedStep > 8 || readOnlyDueToCredit;

        return (
          <Step8FirmaPago ordenId={props.orden} imagenesPago={state.imagenesPago} setImagenesPago={actions.setImagenesPago} estatusFirmaDigital={state.estatusFirmaDigital} isCompleted={state.maxReachedStep > 8} isImmediatePayment={isImmediatePayment} proveedor={state.proveedor} />
        );
      case 9:
        return (
          <Step9Entrega
            imagenesEntrega={state.imagenesEntrega}
            setImagenesEntrega={actions.setImagenesEntrega}
            detallesOT={state.detallesOT}
            evidenciasCierre={state.evidenciasCierre}
            setEvidenciasCierre={actions.setEvidenciasCierre}
            showModalEvidenciasCierre={state.showModalEvidenciasCierre}
            setShowModalEvidenciasCierre={actions.setShowModalEvidenciasCierre}
            evaluacionTaller={state.evaluacionTaller}
            setEvaluacionTaller={actions.setEvaluacionTaller}
            fechaEntregaFormAppointment={state.fechaEntregaFormAppointment}
            setFechaEntregaFormAppointment={actions.setFechaEntregaFormAppointment}
            fechaTerminacionFormAppointment={state.fechaTerminacionFormAppointment}
            setFechaTerminacionFormAppointment={actions.setFechaTerminacionFormAppointment}
            isCompleted={state.maxReachedStep > 9}
            minFechaTerminacion={getMinDateTimeForField("fecha_terminacion", cadena)}
            minFechaEntrega={getMinDateTimeForField("fecha_entrega", {
              ...cadena,
              fecha_terminacion: state.fechaTerminacionFormAppointment || cadena.fecha_terminacion,
            })}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="card shadow-sm border-0 mb-4">
      {Tour}
      <ConfirmModal
        show={state.confirmDialog.show}
        title={state.confirmDialog.title}
        messages={state.confirmDialog.messages}
        message={state.confirmDialog.message}
        confirmLabel={state.confirmDialog.confirmLabel}
        cancelLabel={state.confirmDialog.cancelLabel}
        variant={state.confirmDialog.variant}
        onConfirm={() => actions.resolveConfirmDialog(true)}
        onCancel={() => actions.resolveConfirmDialog(false)}
      />
      {isSysadmin && (
        <div className="alert alert-dark shadow-sm border-dark d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <i className="fas fa-user-shield fa-2x text-warning me-3"></i>
            <div>
              <h6 className="fw-bold mb-0">Panel de Control: SYSADMIN</h6>
              <small className="text-muted">Desbloquea el flujo para corregir pasos anteriores. Se requerirá un motivo de auditoría.</small>
            </div>
          </div>
          <div className="form-check form-switch fs-5 mb-0">
            <input className="form-check-input cursor-pointer" type="checkbox" role="switch" id="sysadminToggle" checked={state.sysadminEditMode} onChange={(e) => actions.setSysadminEditMode(e.target.checked)} />
            <label className="form-check-label fw-bold small ms-2" htmlFor="sysadminToggle">
              {state.sysadminEditMode ? "Edición Desbloqueada" : "Bloqueado"}
            </label>
          </div>
        </div>
      )}
      <div id="ot-stepper-header" className="card-header border-bottom-0 pt-4 pb-0 d-flex flex-wrap justify-content-between align-items-center gap-2">
        <h5 className="fw-bold mb-0">
          <i className="fas fa-route me-2 text-primary"></i>
          {state.isAdicional ? "Seguimiento de Orden de Trabajo Adicional" : "Seguimiento de Orden de Trabajo"}
        </h5>
        <div className="d-flex flex-wrap align-items-center gap-3">
          <TourGuideButtons
            id="ot-stepper-tour-actions"
            onGeneralTour={startGeneralTour}
            onDetailTour={startStepTour}
            detailLabel="¿Cómo funciona este paso?"
          />
          <OrdenCompraDownloadButton
            id="ot-stepper-orden-compra-btn"
            ordenId={props.orden}
            pdfUrl={state.ordenCompraPdfUrl}
            visible={state.tieneOrdenCompra}
          />
          {state.isAdicional && props.otPadreId && (
            <span className="badge bg-secondary">Vinculada a OT Padre: #{props.otPadreId}</span>
          )}
          <button type="button" onClick={actions.refreshData} disabled={state.isLoadingInitial} className="btn btn-sm btn-outline-primary shadow-sm rounded-pill px-3" title="Sincronizar información con el servidor">
            <i className={`fas fa-sync-alt ${state.isLoadingInitial ? "fa-spin" : ""}`}></i>
          </button>
        </div>
      </div>

      <div className="card-body p-4 pt-2">
        {state.isLoadingInitial ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5" style={{ minHeight: "40vh" }}>
            <div className="spinner-border text-primary mb-3" style={{ width: "3rem", height: "3rem" }} role="status"></div>
            <h5 className="text-muted fw-bold">Sincronizando información...</h5>
            <p className="text-muted small">Obteniendo el estatus más reciente de la orden de trabajo</p>
          </div>
        ) : (
          <>
            <div id="ot-stepper-nav" className="d-flex w-100 mt-4 mb-5 pt-4 pb-4 px-0 overflow-auto" style={{ alignItems: "flex-start" }}>
              {state.stepsVisibles.map((step, index) => {
                const isCurrentlyViewed = step.id === state.activeStep;
                const isMaxFrontier = step.id === state.maxReachedStep;
                const isPastCompleted = step.id < state.maxReachedStep;
                const isLocked = step.id > state.maxReachedStep;

                let circleBg = "#e9ecef";
                let iconColor = "#adb5bd";
                let lineBg = "#e9ecef";

                if (isMaxFrontier) {
                  circleBg = "linear-gradient(135deg, #0d6efd 0%, #0dcaf0 100%)";
                  iconColor = "#fff";
                } else if (isPastCompleted) {
                  circleBg = "linear-gradient(135deg, #198754 0%, #20c997 100%)";
                  iconColor = "#fff";
                }

                if (step.id < state.maxReachedStep) {
                  lineBg = "#198754";
                }

                const transformScale = isCurrentlyViewed ? "scale(1.15)" : "scale(1)";
                const shadowBox = isCurrentlyViewed ? isMaxFrontier ? "0 0 15px rgba(13,110,253,0.4)" : "0 0 15px rgba(25,135,84,0.4)" : "none";

                return (
                  <React.Fragment key={step.id}>
                    <div className="d-flex flex-column align-items-center flex-shrink-0" style={{ width: "130px", cursor: isLocked ? "not-allowed" : "pointer" }} onClick={() => actions.handleStepClick(step.id)}>
                      <div className="shadow d-flex justify-content-center align-items-center transition-all" style={{ width: "50px", height: "50px", borderRadius: "50%", background: circleBg, color: iconColor, transform: transformScale, boxShadow: shadowBox, transition: "all 0.3s" }}>
                        {isPastCompleted && !isCurrentlyViewed ? <i className="fas fa-check fs-5"></i> : <i className={`fas ${step.icon} fs-5`}></i>}
                      </div>
                      <div className={`mt-3 text-center px-2 ${isCurrentlyViewed ? "fw-bold " : isPastCompleted || isMaxFrontier ? "text-success fw-bold" : "text-muted"}`} style={{ fontSize: "0.80rem", lineHeight: "1.2" }}>
                        {step.label}
                      </div>
                    </div>
                    {index < state.stepsVisibles.length - 1 && (
                      <div className="flex-grow-1" style={{ height: "4px", backgroundColor: lineBg, marginTop: "23px", minWidth: "30px", borderRadius: "2px", transition: "background-color 0.4s", opacity: isLocked ? 0.6 : 1 }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {state.alert && (
              <div className={`alert alert-${state.alert.type} shadow-sm border-0`}>
                {state.alert.message}
              </div>
            )}

            <div className="p-4 rounded border shadow-sm">
              <h6 className="mb-4 text-secondary border-bottom pb-2">
                Paso Actual: <span className="text-primary fw-bold">{state.stepsVisibles.find((s) => s.id === state.activeStep)?.label || "Completado"}</span>
              </h6>

              {state.activeStep <= 9 ? (
                <>
                  {renderStepContent()}
                  <div id="ot-stepper-bitacora" className="mt-5 pt-4 border-top">
                    <h6 className="fw-bold mb-3"><i className="fas fa-comments text-info me-2"></i> Bitácora de Seguimiento y Eventualidades</h6>
                    <div className="row">
                      <div className="col-md-5 mb-3">
                        <div className="card border-0 shadow-none">
                          <div className="card-body p-3">
                            <label className="fw-bold mb-2 fs-7">Registrar nota de seguimiento</label>
                            <textarea className="form-control form-control-sm mb-2" rows={3} placeholder="Ej: Se solicitó refacción extra..." value={state.nuevoComentario} onChange={(e) => actions.setNuevoComentario(e.target.value)}></textarea>
                            <button type="button" className="btn btn-primary btn-sm w-100 fw-bold" onClick={actions.guardarNotaSeguimiento} disabled={state.isSavingNota || !state.nuevoComentario.trim()}>
                              {state.isSavingNota ? <i className="fas fa-spinner fa-spin"></i> : <><i className="fas fa-paper-plane me-1"></i> Guardar Nota</>}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-7">
                        <div className="overflow-auto pe-2" style={{ maxHeight: "250px" }}>
                          {state.notasSeguimiento.length === 0 ? (
                            <div className="text-center text-muted p-4 fst-italic border rounded">No hay notas registradas aún.</div>
                          ) : (
                            state.notasSeguimiento.map((nota) => {
                              const isSystem = nota.usuario === "SISTEMA";
                              return (
                                <div key={nota.id} className={`border-start border-4 ${isSystem ? "border-success" : "border-info"} p-2 mb-2 rounded shadow-sm`}>
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className={`badge ${isSystem ? "bg-success" : "bg-secondary"}`}>
                                      {isSystem ? <i className="fas fa-cog me-1"></i> : <i className="fas fa-tag me-1"></i>} {nota.nombre_paso}
                                    </span>
                                    <small className="text-muted"><i className="far fa-clock me-1"></i>{nota.fecha}</small>
                                  </div>
                                  <p className={`mb-1 ${isSystem ? "text-success fw-bold" : ""}`} style={{ fontSize: "0.85rem" }}>{nota.comentario}</p>
                                  <small className="text-muted fw-bold"><i className={`fas ${isSystem ? "fa-robot text-success" : "fa-user text-info"} me-1`}></i>{nota.usuario}</small>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr className="text-muted mt-4" />
                  <div className="d-flex justify-content-end mt-4">
                    <button
                      id="ot-stepper-advance-btn"
                      type="button"
                      className="btn btn-success px-5 shadow-sm rounded-pill fw-bold d-flex align-items-center gap-2"
                      onClick={() => {
                        if (state.activeStep < state.maxReachedStep) {
                          const currentIndex = state.stepsVisibles.findIndex((s) => s.id === state.activeStep);
                          const nextStepId = state.stepsVisibles[currentIndex + 1]?.id;
                          if (nextStepId) actions.setActiveStep(nextStepId);
                        } else {
                          actions.avanzarPaso();
                        }
                      }}
                      disabled={state.isProcessing}
                    >
                      {state.isProcessing ? <><i className="fas fa-spinner fa-spin"></i> Procesando...</> : state.activeStep < state.maxReachedStep ? <>Paso Completado (Ver Siguiente) <i className="fas fa-arrow-right"></i></> : <>Avanzar al siguiente paso <i className="fas fa-arrow-right"></i></>}
                    </button>
                  </div>
                </>
              ) : (
                <div className="alert alert-success text-center py-5 shadow-sm border-success">
                  <i className="fas fa-flag-checkered fa-3x mb-3 text-success d-block"></i>
                  <h4 className="fw-bold">¡Excelente Trabajo!</h4>
                  <p className="mb-0">El proceso de esta Orden de Trabajo ha finalizado con éxito.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default OtStepper;